/**
 * Workarounds for Embla behaviour that has no option to configure it. Each one
 * writes to `internalEngine()`, so they are pinned to the installed major.
 */

/**
 * Floating carousel scroll arrow shared by the homepage calendar rails. The left
 * one hangs in the shell's gutter; the right one sits inside a row that bleeds to
 * the viewport edge and has no gutter left to hang in.
 *
 * @param {"left"|"right"} side
 * @returns {object} An MUI `sx` object.
 */
export const railArrowSx = (side) => ({
  position: "absolute",
  [side]: side === "left" ? "-16px" : "8px",
  top: "35%",
  transform: "translateY(-50%)",
  backgroundColor: "paper.primary",
  boxShadow: 2,
  "&:hover": { backgroundColor: "paper.secondary" },
});

/**
 * Pages in a native horizontal scroller; 1 while it has no laid-out width.
 *
 * @param {{ scrollWidth: number, clientWidth: number }} el
 * @returns {number} A finite page count, at least 1.
 */
export const scrollPageCount = (el) =>
  el.clientWidth > 0 ? Math.max(1, Math.round(el.scrollWidth / el.clientWidth)) : 1;

/**
 * Page a native horizontal scroller is showing; 0 while it has no laid-out width.
 *
 * @param {{ scrollLeft: number, clientWidth: number }} el
 * @returns {number} A finite page index.
 */
export const scrollPageIndex = (el) =>
  el.clientWidth > 0 ? Math.round(el.scrollLeft / el.clientWidth) : 0;

/**
 * Index of the snap nearest a scroll offset, so the arrows and the
 * end-of-rail check agree with a position that was restored, not selected.
 *
 * @param {number[]} snaps
 * @param {number} location
 */
const nearestSnapIndex = (snaps, location) =>
  snaps.reduce(
    (nearest, snap, index) =>
      Math.abs(snap - location) < Math.abs(snaps[nearest] - location) ? index : nearest,
    0,
  );

/**
 * Holds the scroll offset across the reInit that appending slides forces. Embla
 * would restore `scrollSnaps[selectedScrollSnap()]`, which lands elsewhere: with
 * `dragFree` the offset is rarely on a snap, and `trimSnaps` re-slices the snap
 * list as content grows, so the same index maps to a different offset.
 *
 * Pass as the `watchSlides` option.
 *
 * @param {import("embla-carousel").EmblaCarouselType} emblaApi
 * @returns {boolean} Always `false` — Embla must not also run its own reInit.
 */
export const preserveOffsetOnSlidesChanged = (emblaApi) => {
  const offset = emblaApi.internalEngine().location.get();
  emblaApi.reInit();

  const engine = emblaApi.internalEngine();
  const restored = engine.limit.constrain(offset);
  engine.index.set(nearestSnapIndex(engine.scrollSnaps, restored));
  for (const vector of [
    engine.location,
    engine.offsetLocation,
    engine.previousLocation,
    engine.target,
  ]) {
    vector.set(restored);
  }
  engine.animation.stop();
  engine.translate.to(restored);
  // `reInit` already fired while the position was still Embla's, so the arrow
  // state was computed against it. Re-announce now that the offset is ours.
  engine.eventHandler.emit("reInit");
  return false;
};

/**
 * How far right the rail has travelled, counting where a `dragFree` flick is
 * going to land rather than where it currently paints.
 *
 * `align: "start"` puts the first snap at 0, so Embla's own offsets run
 * negative — this returns them the other way up.
 *
 * @param {import("embla-carousel").EmblaCarouselType} emblaApi
 * @returns {number}
 */
export const scrolledLead = (emblaApi) => {
  const { offsetLocation, target, limit } = emblaApi.internalEngine();
  return -Math.min(offsetLocation.get(), limit.constrain(target.get()));
};

/**
 * Stops a drag at the carousel's edges instead of letting it pull past and
 * spring back. Embla exposes no option for this — the pull, the spring back and
 * the settle condition are one unit inside `ScrollBounds`.
 *
 * Subscribe on the `scroll` event.
 *
 * @param {import("embla-carousel").EmblaCarouselType} emblaApi
 */
export const clampOverscroll = (emblaApi) => {
  const { limit, target, location, previousLocation, offsetLocation, scrollBody } =
    emblaApi.internalEngine();
  const vectors = [target, location, previousLocation, offsetLocation];
  const atEdge = target.get() === limit.min || target.get() === limit.max;
  if (!atEdge && !vectors.some((vector) => limit.reachedAny(vector.get()))) return;

  // `scroll` fires before the frame interpolates these into the transform, so
  // clamping here lands the edge without ever painting the overshoot.
  for (const vector of vectors) vector.set(limit.constrain(vector.get()));
  // `ScrollBody` keeps a residual velocity that would push the next frame back
  // out. Friction 0 zeroes it on the next seek; drags and `scrollTo` reset it.
  if (target.get() === location.get()) scrollBody.useFriction(0);
};
